import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../../../components/sidebar";
import { useNavigate, useOutletContext } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.scss";
import SearchIcon from "../../../icons/SearchIcon";
import LocationIcon from "../../../icons/LocationIcon";
import SourceIcon from "../../../icons/SourceIcon";
import DestinationIcon from "../../../icons/DestinationIcon";
import BuildingIcon from "../../../icons/BuildingIcon";
import CalendarGreyIcon from "../../../icons/CalendarGreyIcon";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import PlusIcon from "../../../icons/PlusIcon";
import { getMileageList } from "../../../core/data/redux/mileage/_mileageAction";
import { mainCategoryList } from "../../../services/category.service";
import { getPropertyListForFilter } from "../../../core/data/redux/property/_propertyAction";
import { DeleteModalType } from "../../../constants/utlis";
import { formatDateString } from "../../../constants/date";
import { getStorageItem } from "../../../services/storage.service";
import { CommonPagination } from "../../../core/components/common/CommnPagination";
import { apiPageLimit } from "../../../constants/common";

const CpaMileage: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    onToggleSidebar: () => void;
  }>();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  const mileageDetails = useSelector((state: any) => state?.mileage);
  const mileageData = mileageDetails?.mileageList;
  const totalCount = mileageDetails?.totalCount;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const pageSize = apiPageLimit;

  const owner_uuid = getStorageItem("selectedClientUuid");
  console.log("owner_uuid === ", owner_uuid);

  const [mainCategories, setMainCategories] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedMainCategory, setSelectedMainCategory] = useState<string>("");
  const [allProperties, setAllProperties] = useState<any[]>([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch mileage list on component mount
  useEffect(() => {
    dispatch(getMileageList({ owner_uuid }));
    getMainCategoryList();
    getAllPropertyList();
  }, [dispatch]);

  // get main category list
  const getMainCategoryList = async () => {
    try {
      const response = await mainCategoryList();
      const data = response?.data?.data || [];

      const mapped = data.map((cat: any) => ({
        label: cat.name,
        value: cat.uuid,
      }));

      setMainCategories([{ label: "Choose Activity", value: "" }, ...mapped]);
      return;
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({ owner_uuid }));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  };

  // Debounce search
  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Fetch properties with debounce
  const fetchProperties = useCallback(
    debounce(
      (
        search: string,
        selectedMainCategory: string,
        page: number,
        selectedProperty: string,
        owner_uuid: string
      ) => {
        const params: any = { page, limit: pageSize };
        if (search) params.search = search;
        if (selectedMainCategory) params.main_category = selectedMainCategory;
        if (selectedProperty) params.property = selectedProperty;
        if (owner_uuid) params.owner_uuid = owner_uuid;
        dispatch(getMileageList(params));
      },
      500
    ),
    [dispatch]
  );

  // Run when searchTerm or selected changes
  useEffect(() => {
    fetchProperties(
      searchTerm,
      selectedMainCategory,
      page,
      selectedProperty,
      owner_uuid
    );
  }, [
    searchTerm,
    selectedMainCategory,
    page,
    selectedProperty,
    owner_uuid,
    fetchProperties,
  ]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="layout">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings mileage-tracking-page-section">
          <div className="rounded mileage-tracking-page-inner">
            <div className="add-properties-heading d-flex justify-content-between gap-3 mb-4">
              <h2 className="mb-0">Mileage Tracking</h2>
            </div>

            <div className="row g-3 mb-3 row-boder-line">
              {/* Search log hours */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 col-xxl-6">
                <div className="input-group mb-3 commonInput bg-light bg-white">
                  <span className="input-group-text bg-white border-end-0 ">
                    <SearchIcon />
                  </span>
                  <input
                    type="text"
                    placeholder="Search trip by purpose or route"
                    className="name form-control border-start-0"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Category select */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-3">
                <select
                  className="form-select form-control commonInput"
                  value={selectedMainCategory}
                  onChange={(e) => setSelectedMainCategory(e.target.value)}
                >
                  <option value="" disabled hidden>
                    Choose Activity
                  </option>

                  {mainCategories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Property dropdown */}
              <div
                className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-2 col-xxl-3"
                ref={dropdownRef}
              >
                <div className="dropdown log-hour-list-page-dropdown">
                  <button
                    className="form-select d-flex justify-content-between align-items-center form-control commonInput"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    {selectedPropertyName || "Select Property "}
                    <i
                      className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div
                      className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                      style={{ maxHeight: "initial" }}
                    >
                      <div className="input-group mb-3 commonInput bg-light bg-white">
                        <span className="input-group-text bg-white border-end-0 ">
                          <SearchIcon />
                        </span>
                        <input
                          type="text"
                          placeholder="Search properties by name or address"
                          className="name form-control border-start-0"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>

                      {filteredProperties.length > 0 ? (
                        filteredProperties.map((property, index) => (
                          <div
                            key={property?.uuid || index}
                            className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput bg-light"
                            onClick={() => {
                              setSelectedProperty(property?.uuid);
                              setSelectedPropertyName(property?.name);
                              setIsDropdownOpen(false);
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <label className="form-check-label ms-2">
                              <div className="label-inner-content d-flex flex-column gap-2">
                                <strong>{property.name}</strong>
                                <small className="text-muted d-flex align-items-center">
                                  <LocationIcon />
                                  {property.address}, {property?.city},{" "}
                                  {property?.state} {property?.zipcode}
                                </small>
                              </div>
                            </label>
                            <input
                              type="radio"
                              className="form-check-input mt-1"
                              name="propertyRadio"
                              checked={selectedPropertyName === property?.name}
                              readOnly
                            />
                          </div>
                        ))
                      ) : (
                        <div className="text-muted text-center">
                          No results found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="row align-items-stretch">
              {mileageData?.length > 0 ? (
                mileageData?.map((mileage: any) => {
                  return (
                    <div className="mb-4 col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-3 ">
                      <div className="custom-card mx-auto h-100">
                        <div className="card-body">
                          <div className="d-flex gap-2 justify-content-between align-items-center flex-wrap">
                            <span
                              className={
                                mileage?.main_category?.name.toLowerCase() ==
                                "material participation"
                                  ? "category-badge"
                                  : "category-badge yellow"
                              }
                            >
                              {mileage?.main_category?.name}
                            </span>

                            <div className="d-flex justify-content-end  time-text">
                              <span className="time-badge">
                                {mileage?.distance} mi
                              </span>
                            </div>
                          </div>

                          <div className="mt-4 text-area d-flex justify-content-between align-items-center  flex-wrap gap-2">
                            <h5 className=" fw-bold mb-0 d-flex  flex-column">
                              <p className="home">
                                <SourceIcon />
                                {mileage?.source_name}
                              </p>
                              <p className="ocean">
                                <DestinationIcon />
                                {mileage?.destination_name}
                              </p>
                            </h5>
                          </div>
                          <p className="mb-1 mt-1  mileage-name">
                            {mileage?.sub_activity?.name}
                          </p>
                          <div className="mt-3 ">
                            <span className="tag tag-red mb-2">
                              <BuildingIcon /> {mileage?.property?.name}
                            </span>
                            <span className="tag tag-blue">
                              <CalendarGreyIcon />{" "}
                              {formatDateString(mileage?.date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center w-100 py-4">No data found.</p>
              )}
            </div>

            {/* Pagination */}
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default CpaMileage;
